import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const FeaturedPropertyCard = ({
  featuredProperty,
  isSelected = false,
  onSelect,
  onEdit,
  onDelete,
  onToggleStatus,
  isAuthorized = false
}) => {
  const formatPrice = (price) => {
    if (!price) return 'Prix non défini';
    const numPrice = parseInt(price);
    const formatted = (numPrice / 1000000)?.toFixed(0);
    return `${formatted}M XOF`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`bg-white rounded-lg shadow-warm hover:shadow-deep transition-all duration-300 overflow-hidden ${
      isSelected ? 'ring-2 ring-brand-earth' : ''
    }`}>
      {/* Property Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={
            featuredProperty?.hero_image_url || 
            featuredProperty?.property?.images?.[0] || 
            '/assets/images/no_image.png'
          }
          alt={featuredProperty?.custom_headline || featuredProperty?.property?.title}
          className="w-full h-full object-cover"
        />
        
        {/* Status Badge */}
        <div className="absolute top-4 left-4">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            featuredProperty?.is_active
              ? 'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
          }`}>
            {featuredProperty?.is_active ? 'Actif' : 'Inactif'}
          </span>
        </div>

        {/* Display Order Badge */}
        <div className="absolute top-4 right-4">
          <span className="bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 text-xs font-medium text-text-primary">
            #{featuredProperty?.display_order}
          </span>
        </div>

        {/* Selection Checkbox */}
        {isAuthorized && (
          <div className="absolute bottom-4 left-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onSelect}
              className={`p-1 h-8 w-8 bg-white/90 backdrop-blur-sm hover:bg-white ${
                isSelected ? 'text-brand-earth' : 'text-text-secondary'
              }`}
              iconName={isSelected ? "CheckSquare" : "Square"}
              title={isSelected ? "Désélectionner" : "Sélectionner"}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Title and Location */}
        <div className="mb-3">
          <h3 className="font-semibold text-lg text-text-primary mb-1 line-clamp-1">
            {featuredProperty?.custom_headline || featuredProperty?.property?.title}
          </h3>
          <div className="flex items-center text-text-secondary text-sm">
            <Icon name="MapPin" size={14} className="mr-1" />
            <span className="line-clamp-1">
              {featuredProperty?.property?.address}
              {featuredProperty?.property?.commune && ` • ${featuredProperty?.property?.commune?.name}`}
            </span>
          </div>
        </div>

        {/* Promotional Text */}
        {featuredProperty?.promotional_text && (
          <div className="mb-3">
            <p className="text-sm text-brand-gold italic line-clamp-2">
              {featuredProperty?.promotional_text}
            </p>
          </div>
        )}

        {/* Property Details */}
        <div className="grid grid-cols-3 gap-2 text-sm text-text-secondary mb-3">
          <div className="flex items-center">
            <Icon name="Bed" size={14} className="mr-1" />
            {featuredProperty?.property?.bedrooms || 0}
          </div>
          <div className="flex items-center">
            <Icon name="Bath" size={14} className="mr-1" />
            {featuredProperty?.property?.bathrooms || 0}
          </div>
          <div className="flex items-center">
            <Icon name="Square" size={14} className="mr-1" />
            {featuredProperty?.property?.surface_area || 0}m²
          </div>
        </div>

        {/* Pricing */}
        <div className="flex items-center justify-between mb-4">
          <div>
            {featuredProperty?.promotional_price ? (
              <div>
                <span className="text-brand-earth font-bold text-lg">
                  {formatPrice(featuredProperty?.promotional_price)}
                </span>
                <span className="text-text-secondary text-sm ml-2 line-through">
                  {formatPrice(featuredProperty?.property?.price)}
                </span>
              </div>
            ) : (
              <span className="text-brand-earth font-bold text-lg">
                {formatPrice(featuredProperty?.property?.price)}
              </span>
            )}
          </div>
          
          <div className="text-xs text-text-secondary">
            <span className="uppercase">{featuredProperty?.property?.property_type}</span>
          </div>
        </div>

        {/* Analytics */}
        <div className="grid grid-cols-2 gap-4 text-center text-sm mb-4 py-3 bg-surface rounded-lg">
          <div>
            <div className="font-semibold text-text-primary">
              {featuredProperty?.click_count || 0}
            </div>
            <div className="text-text-secondary text-xs">Clics</div>
          </div>
          <div>
            <div className="font-semibold text-text-primary">
              {featuredProperty?.inquiry_count || 0}
            </div>
            <div className="text-text-secondary text-xs">Demandes</div>
          </div>
        </div>

        {/* Dates */}
        <div className="text-xs text-text-secondary mb-4 space-y-1">
          <div className="flex justify-between">
            <span>Début:</span>
            <span>{formatDate(featuredProperty?.start_date)}</span>
          </div>
          {featuredProperty?.end_date && (
            <div className="flex justify-between">
              <span>Fin:</span>
              <span>{formatDate(featuredProperty?.end_date)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Créé:</span>
            <span>{formatDate(featuredProperty?.created_at)}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            variant="outline" 
            size="sm"
            onClick={onEdit}
            disabled={!isAuthorized}
            iconName="Edit2"
            className="flex-1"
            title={!isAuthorized ? 'Permissions insuffisantes' : 'Modifier'}
          >
            Modifier
          </Button>
          
          <Button
            variant={featuredProperty?.is_active ? "ghost" : "default"}
            size="sm"
            onClick={onToggleStatus}
            disabled={!isAuthorized}
            iconName={featuredProperty?.is_active ? "EyeOff" : "Eye"}
            title={featuredProperty?.is_active ? 'Désactiver' : 'Activer'}
          />
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            disabled={!isAuthorized}
            iconName="Trash2"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            title={!isAuthorized ? 'Permissions insuffisantes' : 'Supprimer'}
          />
        </div>
      </div>
    </div>
  );
};

export default FeaturedPropertyCard;